import { useState } from "react";
import Button from "../Button";
import { isMac } from "../../utils";
import * as Tooltip from "@radix-ui/react-tooltip";

const ViewerHeader = ({
  onRun,
  onRefresh,
  onTabChange,
  selectedTab,
}: {
  onRun: () => void;
  onRefresh: () => void;
  selectedTab: "Preview" | "Browser Inspector";
  onTabChange: (tab: "Preview" | "Browser Inspector") => void;
}) => {
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const Link = ({
    href,
    children,
    localHref,
    target,
    underline = false,
  }: {
    href: string;
    children: React.ReactNode;
    localHref?: string;
    target?: string;
    underline?: boolean;
  }) => {
    const isLocalHost = window.location.hostname === "localhost";

    return (
      <a
        href={isLocalHost ? localHref : href}
        target={target || "_self"}
        rel="noopener noreferrer"
        className={`text-text-primary ${underline ? "underline underline-offset-2" : ""}`}
      >
        {children}
      </a>
    );
  };

  const ButtonTab = ({
    icon,
    text,
  }: {
    icon: React.ReactNode;
    text: string;
  }) => {
    return (
      <div
        title={text}
        onClick={() => {
          onTabChange(text as any);
        }}
        className={`transition-colors cursor-pointer flex items-center py-2 px-2.5 rounded-xl gap-1 ${selectedTab === text ? "bg-[#F0F2F5]" : "bg-none hover:bg-[#f9f7f7]"}`}
      >
        <span className="text-sm mr-1">{icon}</span>
        <span className="text-sm hidden md:inline">{text}</span>
      </div>
    );
  };

  return (
    <div className="andrewtest flex flex-wrap justify-between items-center bg-white rounded-t-2xl pl-3 py-2 gap-y-2 min-h-[52px]">
      <div className="flex gap-2">
        <ButtonTab
          icon={
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M1.875 5C1.875 4.33696 2.13839 3.70107 2.60723 3.23223C3.07607 2.76339 3.71196 2.5 4.375 2.5H15.625C16.288 2.5 16.9239 2.76339 17.3928 3.23223C17.8616 3.70107 18.125 4.33696 18.125 5V15C18.125 15.663 17.8616 16.2989 17.3928 16.7678C16.9239 17.2366 16.288 17.5 15.625 17.5H4.375C3.71196 17.5 3.07607 17.2366 2.60723 16.7678C2.13839 16.2989 1.875 15.663 1.875 15V5ZM16.875 7.5H3.125V15C3.125 15.3315 3.2567 15.6495 3.49112 15.8839C3.72554 16.1183 4.04348 16.25 4.375 16.25H15.625C15.9565 16.25 16.2745 16.1183 16.5089 15.8839C16.7433 15.6495 16.875 15.3315 16.875 15V7.5ZM4.375 4.375C4.20924 4.375 4.05027 4.44085 3.93306 4.55806C3.81585 4.67527 3.75 4.83424 3.75 5V5.00667C3.75 5.35167 4.03 5.63167 4.375 5.63167H4.38167C4.54743 5.63167 4.7064 5.56582 4.82361 5.44861C4.94082 5.3314 5.00667 5.17243 5.00667 5.00667V5C5.00667 4.83424 4.94082 4.67527 4.82361 4.55806C4.7064 4.44085 4.54743 4.375 4.38167 4.375H4.375ZM5.625 5C5.625 4.83424 5.69085 4.67527 5.80806 4.55806C5.92527 4.44085 6.08424 4.375 6.25 4.375H6.25667C6.42243 4.375 6.5814 4.44085 6.69861 4.55806C6.81582 4.67527 6.88167 4.83424 6.88167 5V5.00667C6.88167 5.17243 6.81582 5.3314 6.69861 5.44861C6.5814 5.56582 6.42243 5.63167 6.25667 5.63167H6.25C6.08424 5.63167 5.92527 5.56582 5.80806 5.44861C5.69085 5.3314 5.625 5.17243 5.625 5.00667V5ZM8.125 4.375C7.95924 4.375 7.80027 4.44085 7.68306 4.55806C7.56585 4.67527 7.5 4.83424 7.5 5V5.00667C7.5 5.35167 7.78 5.63167 8.125 5.63167H8.13167C8.29743 5.63167 8.4564 5.56582 8.57361 5.44861C8.69082 5.3314 8.75667 5.17243 8.75667 5.00667V5C8.75667 4.83424 8.69082 4.67527 8.57361 4.55806C8.4564 4.44085 8.29743 4.375 8.13167 4.375H8.125Z"
                fill="#414552"
              />
            </svg>
          }
          text="Preview"
        />
        <ButtonTab
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              fill="none"
              stroke="#414552"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="2" y1="12" x2="22" y2="12" />
              <path d="M12 2a15.3 15.3 0 0 1 0 20" />
              <path d="M12 2a15.3 15.3 0 0 0 0 20" />
            </svg>
          }
          text="Browser Inspector"
        />
      </div>

      <div className="flex gap-2">
        <span
          className="flex items-center gap-1 cursor-pointer"
          onClick={() => {
            setIsHelpOpen(!isHelpOpen);
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16ZM7 8.0885C6.9971 8.05683 6.99567 8.02474 6.99578 7.9923C6.9977 7.43768 7.44886 6.98963 8.00347 6.99155C8.21663 6.99228 8.4251 6.94784 8.61678 6.86046C8.77658 6.78762 8.92403 6.68535 9.05469 6.55469C9.63718 5.9722 9.63718 5.0278 9.05469 4.44531C8.7679 4.15852 8.39576 4.00735 8.0028 4.00845C7.60784 4.00955 7.22683 4.16379 6.94531 4.44531C6.65184 4.73878 6.50467 5.09958 6.50852 5.4901C6.51398 6.04469 6.06883 6.49871 5.51424 6.50417C4.95965 6.50964 4.50564 6.06449 4.50017 5.5099C4.49101 4.5802 4.84992 3.70034 5.52513 3.02513C6.18686 2.36339 7.07816 2.00258 7.9972 2.00001C8.92088 1.99744 9.80722 2.35748 10.4749 3.02513C11.8417 4.39196 11.8417 6.60804 10.4749 7.97487C10.1744 8.27539 9.82802 8.5156 9.44984 8.68799C9.28011 8.76536 9.10507 8.82855 8.92636 8.87733C8.77744 9.24257 8.41878 9.5 8 9.5C7.44772 9.5 7 9.05229 7 8.5V8.0885ZM6.5 12.5C6.5 13.3284 7.17157 14 8 14C8.82843 14 9.5 13.3284 9.5 12.5C9.5 11.6716 8.82843 11 8 11C7.17157 11 6.5 11.6716 6.5 12.5Z"
              fill="#414552"
            />
          </svg>
          <span className="hidden md:inline">Help</span>
        </span>
        <Button onClick={onRun}>
          <span className="hidden sm:inline">
            {isMac() ? "⌘+S" : "Ctrl+S"}{" "}
          </span>
          Run
        </Button>
        <Button onClick={() => setIsWarningOpen(true)}>Reset</Button>
        <Tooltip.Provider>
          <Tooltip.Root open={isHelpOpen}>
            <Tooltip.Trigger />
            <Tooltip.Portal>
              <Tooltip.Content
                onPointerDownOutside={() => isHelpOpen && setIsHelpOpen(false)}
                onEscapeKeyDown={() => isHelpOpen && setIsHelpOpen(false)}
                side="bottom"
                align="end"
                className="bg-white justify-between flex flex-col rounded-md shadow-lg border max-w-sm  p-2 border-border z-50 relative"
                sideOffset={5}
              >
                <div className="hidden md:flex flex-col justify-end    p-2">
                  <span className="text-xs text-text-secondary mt-0 font-bold mb-2">
                    This is an example of a Playwright automation script and our
                    integrateable Frame.
                  </span>{" "}
                  <span className="text-xs text-text-secondary">
                    Click{" "}
                    <Button
                      className="text-xs"
                      style={{ padding: "0px 8px", marginRight: 2 }}
                      onClick={onRun}
                    >
                      Run
                    </Button>{" "}
                    to see the Frame for this script. Visit our{" "}
                    <Link
                      target="pluto-docs"
                      href="https://docs.pluto.xyz"
                      localHref="https://docs.pluto.xyz"
                      underline
                    >
                      docs
                    </Link>{" "}
                    to learn how to integrate into your application
                  </span>
                </div>
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>

          <Tooltip.Root open={isWarningOpen}>
            <Tooltip.Trigger />
            <Tooltip.Portal>
              <Tooltip.Content
                onPointerDownOutside={() => setIsWarningOpen(false)}
                onEscapeKeyDown={() => setIsWarningOpen(false)}
                side="bottom"
                align="end"
                className="bg-white justify-between flex flex-col rounded-md shadow-lg border min-w-[415px] max-w-[415px] min-h-[133px] max-h-[133px] p-4 border-border z-50"
                sideOffset={5}
              >
                <div className="flex items-start gap-2">
                  <svg
                    width="12"
                    height="12"
                    transform="scale(1)"
                    viewBox="0 0 12 12"
                    className="mt-1"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g id="info">
                      <path
                        id="Glyph"
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M6.75 6C6.75 5.58579 6.41421 5.25 6 5.25H4.125C3.71079 5.25 3.375 5.58579 3.375 6C3.375 6.41421 3.71079 6.75 4.125 6.75H5.25V9.75C5.25 10.1642 5.58579 10.5 6 10.5C6.41421 10.5 6.75 10.1642 6.75 9.75V6ZM3 0H9C10.6569 0 12 1.34315 12 3V9C12 10.6569 10.6569 12 9 12H3C1.34315 12 0 10.6569 0 9V3C0 1.34315 1.34315 0 3 0ZM6 4.125C6.62132 4.125 7.125 3.62132 7.125 3C7.125 2.37868 6.62132 1.875 6 1.875C5.37868 1.875 4.875 2.37868 4.875 3C4.875 3.62132 5.37868 4.125 6 4.125Z"
                        fill="#C84801"
                      />
                    </g>
                  </svg>
                  <div className="flex flex-col">
                    <div className="text-text-primary font-semibold text-sm flex items-center gap-3">
                      <span>This will override any existing changes</span>
                    </div>
                    <span className="text-text-secondary text-sm tracking-normal">
                      Your editor will reset to the new version of the script.
                    </span>
                  </div>
                  <span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      onClick={() => setIsWarningOpen(false)}
                      className="cursor-pointer"
                    >
                      <g id="cancel">
                        <path
                          id="Glyph"
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M8.00061 6.58435L12.5926 1.99238C12.9835 1.6015 13.6172 1.6015 14.0081 1.99238C14.399 2.38327 14.399 3.01702 14.0081 3.40791L9.41678 7.99922L14.0081 12.5905C14.399 12.9814 14.399 13.6152 14.0081 14.0061C13.6172 14.3969 12.9835 14.3969 12.5926 14.0061L8.00061 9.41409L3.40864 14.0061C3.01775 14.3969 2.384 14.3969 1.99312 14.0061C1.60223 13.6152 1.60223 12.9814 1.99312 12.5905L6.58443 7.99922L1.99312 3.40791C1.60223 3.01702 1.60223 2.38327 1.99312 1.99238C2.384 1.6015 3.01775 1.6015 3.40864 1.99238L8.00061 6.58435Z"
                          fill="#6A7383"
                        />
                      </g>
                    </svg>
                  </span>
                </div>
                <div className="flex gap-2 mt-1 justify-end">
                  <Button onClick={() => setIsWarningOpen(false)}>
                    <span className="inline-block min-w-[64px] w-[64px] text-center">
                      Cancel
                    </span>
                  </Button>
                  <Button
                    onClick={() => {
                      setIsWarningOpen(false);
                      onRefresh();
                    }}
                  >
                    <span className="inline-block min-w-[64px] w-[64px] text-center">
                      Continue
                    </span>
                  </Button>
                </div>
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      </div>
    </div>
  );
};

export default ViewerHeader;
